import { useTranslation } from 'react-i18next';
import { useLocationSports } from '@/api/hooks/useLocations';
import { useVenueBySlug } from '@/api/hooks/useVenues';
import { DEFAULT_VENUE_SLUG } from '@/lib/constants';
import { Skeleton } from '@/components/ui/skeleton';
import SportCard from '@/components/booking/SportCard';
import { useVenueSlugCapture } from '@/hooks/useVenueSlugCapture';

export default function HomePage() {
  useVenueSlugCapture();
  const { t } = useTranslation();
  const { data: venue, isLoading: venueLoading, isError: venueError } = useVenueBySlug(DEFAULT_VENUE_SLUG);
  const { data, isLoading: sportsLoading, isError: sportsError } = useLocationSports(venue?.id);
  const isLoading = venueLoading || sportsLoading;
  const isError = venueError || sportsError;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      {isLoading && (
        <>
          <Skeleton className="mb-2 h-8 w-1/3" />
          <Skeleton className="mb-8 h-4 w-1/2" />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-48 rounded-xl" />
            ))}
          </div>
        </>
      )}

      {isError && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {t('common.error')}
        </div>
      )}

      {data && (
        <>
          {/* Hero section */}
          <div className="-mx-4 -mt-8 mb-8 rounded-b-3xl bg-gradient-to-br from-primary/10 via-background to-primary/5 px-4 py-12 text-center">
            <h1 className="text-3xl font-bold">{data.location.name}</h1>
            <p className="mt-1 text-muted-foreground">
              {data.location.address}, {data.location.city}, {data.location.province}
            </p>
            <p className="mt-3 text-lg text-primary font-medium">
              {t('booking.hero_tagline')}
            </p>
          </div>

          {/* #89 — a paused facility shows an honest unavailable message instead of bookable
              sport cards, so the customer learns at browse time (not at payment). No return-time
              promise: since #145 removed auto-unpause, the pause holds until an operator resumes. */}
          {data.location.bookingsPaused ? (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 px-6 py-12 text-center">
              <p className="text-lg font-semibold text-amber-900">{t('booking.facility_paused_title')}</p>
              <p className="mt-2 text-sm text-amber-800">{t('booking.facility_paused_body')}</p>
            </div>
          ) : (
            <>
              <h2 className="mb-4 text-lg font-semibold">{t('booking.select_sport')}</h2>

              {data.sports.length === 0 ? (
                <p className="py-12 text-center text-muted-foreground">
                  {t('booking.no_sports', 'No activities available')}
                </p>
              ) : (
                <div className="rounded-2xl bg-primary/[0.02] p-6 -mx-2">
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {data.sports.map((sport) => (
                      <SportCard key={sport.code} sport={sport} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
